import { DurationInput } from "@formatjs/intl-durationformat"
import { IconName } from "@inflow/icons"
import {
  DateFormatter,
  getLocalTimeZone,
  now,
  parseAbsolute,
  parseAbsoluteToLocal,
  parseTime,
  toCalendarDateTime,
  ZonedDateTime,
} from "@internationalized/date"
import { NumberFormatter } from "@internationalized/number"
import { MakeRouteMatchUnion } from "@tanstack/react-router"
import { StripePaymentMethod } from "ipctg-inflow-types/InFlowAPIMisc"
import { CardBrand, ReservationStatus } from "ipctg-inflow-types/InFlowEnums"
import { Hue, HueSteps, Theme } from "ipctg-inflow-types/InFlowThemes"
import {
  RefCallback,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react"

import { US_LOCALE } from "./localization"

export const UNKNOWN_PLATE_PREFIX = "UNKNOWN"

export const cn = (...objs: any) => objs.flat().filter(Boolean).join(" ")

export const stripEmptyParams = <T extends Record<string, unknown>>(
  search: T
) => {
  return Object.entries(search).reduce(
    (acc, [key, value]) => {
      if (
        value === undefined ||
        value === "" ||
        (typeof value === "number" && isNaN(value))
      ) {
        delete acc[key]
      }
      return acc
    },
    { ...search }
  )
}

export function cleanEmptyParams<T extends Record<string, unknown>>(search: T) {
  return Object.entries(search).reduce(
    (acc, [key, value]) => {
      if (
        value === undefined ||
        value === "" ||
        (typeof value === "number" && isNaN(value))
      ) {
        delete acc[key]
      }
      return acc
    },
    { ...search }
  )
}

// TODO fix me, we should get this from i18n, not from navigator
export function getLocale() {
  return typeof window !== "undefined" ? navigator.language : US_LOCALE
}

export function formatZonedDateTimeWithTimeZone(zdt: ZonedDateTime) {
  const dateFormatter = new DateFormatter(getLocale(), {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: zdt.timeZone,
  })
  const timeFormatter = new DateFormatter(getLocale(), {
    hour: "numeric",
    minute: "numeric",
    timeZone: zdt.timeZone,
    timeZoneName: "short",
  })

  return `${dateFormatter.format(zdt.toDate())}, ${timeFormatter.format(zdt.toDate())}`
}

export function formatDateForDisplay(args: {
  isoDateString: string
  locale: string
  monthFormat?: "long" | "short"
  timeZone?: string
}): string {
  const zonedDate = args.timeZone
    ? parseAbsolute(args.isoDateString, args.timeZone)
    : parseAbsoluteToLocal(args.isoDateString)
  const formatter = new DateFormatter(args.locale, {
    year: "numeric",
    month: args.monthFormat || "long",
    day: "numeric",
    timeZone: zonedDate.timeZone,
  })

  return formatter.format(zonedDate.toDate())
}

/**
 * Formats a future time based on minutes from now
 * @param minutes - Number of minutes to add to current time
 * @returns Formatted time string (e.g., "3:30 PM")
 */
export function formatTimeFromMinutes(minutes: number): string {
  const userTimeZone = getLocalTimeZone()
  const currentTime = now(userTimeZone)
  const endTime = currentTime.add({ minutes })
  const endDateTime = toCalendarDateTime(endTime)

  const timeFormatter = new DateFormatter(getLocale(), {
    hour: "numeric",
    minute: "2-digit",
    timeZone: userTimeZone,
  })

  return timeFormatter.format(endDateTime.toDate(userTimeZone))
}

/**
 * Formats a time string (HH:mm:ss) to a localized time display
 * @param timeString - Time in HH:mm:ss format (e.g., "18:00:00")
 * @param locale - Locale for formatting
 * @returns Formatted time string (e.g., "6:00 PM")
 */
export function formatTimeOfDay(timeString: string, locale?: string): string {
  const time = parseTime(timeString)

  const timeFormatter = new DateFormatter(locale || getLocale(), {
    hour: "numeric",
    minute: "2-digit",
    timeZone: getLocalTimeZone(),
  })

  return timeFormatter.format(
    now(getLocalTimeZone())
      .set({ hour: time.hour, minute: time.minute })
      .toDate()
  )
}

/**
 * Translates a DayOfWeek enum value to a localized full day name
 * HACKY REWORK NEEDED
 * @param dayOfWeek - Abbreviated day (e.g., "Sun", "Mon")
 * @param locale - Locale for formatting
 * @returns Localized full day name (e.g., "Sunday", "lunes", "Montag")
 */
export function getTranslatedDayName(
  dayOfWeek: string,
  locale?: string
): string {
  // Maps actual days from Jan 2024 that we know fall on that day
  // Map abbreviated day to a date that falls on that day (Jan 2024)
  const dayToDate: Record<string, number> = {
    Sun: 7,
    Mon: 8,
    Tue: 9,
    Wed: 10,
    Thu: 11,
    Fri: 12,
    Sat: 13,
  }

  const date = dayToDate[dayOfWeek]
  if (!date) return dayOfWeek

  return new Intl.DateTimeFormat(locale || getLocale(), {
    weekday: "long",
  }).format(new Date(2024, 0, date))
}

/**
 * Formats a date string to include date, time, and timezone
 * Wrapper around formatZonedDateTimeWithTimeZone that accepts ISO string
 * @param dateString - ISO date string
 * @returns Formatted string (e.g., "Oct 21, 2025, 5:42 PM CDT")
 */
export function formatDateTimeWithTimeZone(dateString: string): string {
  const zonedDate = parseAbsoluteToLocal(dateString)
  return formatZonedDateTimeWithTimeZone(zonedDate)
}

/**
 * Gets the current epoch time in milliseconds using the user's local timezone
 * This ensures proper timezone handling when generating timestamps for debugging
 * @returns Epoch milliseconds as a string
 */
export function getCurrentEpochMillis(): string {
  // Get the current ZonedDateTime in the user's local time zone
  const zonedNow = now(getLocalTimeZone())

  // Convert to a real JavaScript Date (this applies the timezone offset)
  const jsDate = zonedNow.toDate()

  // Get epoch milliseconds
  const epochMs = jsDate.getTime()

  return epochMs.toString()
}

export function percentFormatter(amount: number) {
  const percentFormatter = new NumberFormatter(getLocale(), {
    style: "percent",
    maximumFractionDigits: 3,
  })

  return percentFormatter.format(amount)
}

export function upperFirst(str: string): string {
  if (!str) {
    return str
  }
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function startCase(input: string): string {
  const replaced = input.replace(/[_-]+/g, " ")
  const withSpaces = replaced.replace(/([a-z])([A-Z])/g, "$1 $2")

  return withSpaces
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
}

export function camelCase(input: string): string {
  const replaced = input.replace(/[_-]+/g, " ")
  const withSpaces = replaced.replace(/([a-z])([A-Z])/g, "$1 $2")
  const words = withSpaces
    .trim()
    .split(/\s+/)
    .map((word) => word.toLowerCase())

  if (words.length === 0) return ""

  return (
    words[0] +
    words
      .slice(1)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("")
  )
}

export function apiCardBrandToIconName(
  paymentMethod: StripePaymentMethod
): IconName {
  switch (paymentMethod.cardBrand) {
    case CardBrand.Visa:
      return "paymentVisa"
    case CardBrand.Mastercard:
      return "paymentMastercard"
    case CardBrand.AmericanExpress:
      return "paymentAmericanExpress"
    case CardBrand.Discover:
      return "paymentDiscover"
    default:
      return "creditCard"
  }
}

export function getStr(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined
  const s = v.trim()
  return s ? s : undefined
}

export function asBool(v: unknown): true | undefined {
  if (v === true) return true
  if (typeof v === "string" && v.trim().toLowerCase() === "true") return true
  return undefined
}

export function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0
}

export function hueToSwatch(args: { theme: Theme; hue: Hue; step: HueSteps }) {
  return args.theme.hues[args.hue][args.step]
}

// Unexported Util function from RAC
export function useSlot(): [RefCallback<Element>, boolean] {
  // Assume we do have the slot in the initial render.
  const [hasSlot, setHasSlot] = useState(true)
  const hasRun = useRef(false)

  // A callback ref which will run when the slotted element mounts.
  // This should happen before the useLayoutEffect below.
  const ref = useCallback((el: any) => {
    hasRun.current = true
    setHasSlot(!!el)
  }, [])

  // If the callback hasn't been called, then reset to false.
  useLayoutEffect(() => {
    if (!hasRun.current) {
      setHasSlot(false)
    }
  }, [])

  return [ref, hasSlot]
}

export function toDuration(args: {
  intervalCount: number
  intervalPeriod: "Minute" | "Hour" | "Day" | "CalendarDay"
}): DurationInput {
  if (args.intervalCount < 0) {
    throw new Error("intervalCount must be a positive number")
  }

  let totalMinutes: number

  switch (args.intervalPeriod) {
    case "Minute":
      totalMinutes = args.intervalCount
      break
    case "Hour":
      totalMinutes = args.intervalCount * 60
      break
    case "Day":
    case "CalendarDay":
      totalMinutes = args.intervalCount * 24 * 60
      break
  }

  // Handle floating-point precision safely
  totalMinutes = Math.round(totalMinutes)

  const minutesInDay = 24 * 60
  const minutesInHour = 60

  const days = Math.floor(totalMinutes / minutesInDay)
  const remainingAfterDays = totalMinutes % minutesInDay

  const hours = Math.floor(remainingAfterDays / minutesInHour)
  const minutes = remainingAfterDays % minutesInHour

  const duration: DurationInput = {
    days,
    hours,
    minutes,
  }

  return duration
}

/**
 * The single source of truth for "this reservation was cancelled". Shared so
 * the reservations list and the card badge cannot disagree about which
 * statuses count as cancelled.
 */
export function isCancelledStatus(status: ReservationStatus): boolean {
  return (
    status === ReservationStatus.CancelledBySelf ||
    status === ReservationStatus.CancelledByAdmin
  )
}

/*
  For QuotaPack Passes, we show the last 8 characters as an ID for the pass.
  Some locations abuse this product it as a ticketing/seasonal pass system.
*/
export function prettyPassID(passID: string) {
  if (passID.length < 8) return passID.toUpperCase()
  return passID.slice(-8).toUpperCase()
}

/** The first of `matches` on one of `paths`, narrowed so its `params` are typed. */
export function matchToRoute<
  TPaths extends readonly MakeRouteMatchUnion["routeId"][],
>(args: { matches: MakeRouteMatchUnion[]; paths: TPaths }) {
  return args.matches.find(
    (m): m is Extract<MakeRouteMatchUnion, { routeId: TPaths[number] }> =>
      args.paths.includes(m.routeId)
  )
}

export function matchesToRoutes<
  TPaths extends readonly MakeRouteMatchUnion["routeId"][],
>(args: { matches: MakeRouteMatchUnion[]; paths: TPaths }) {
  return args.matches.some((m) => args.paths.includes(m.routeId))
}
